import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { useForm, type SubmitErrorHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AxiosError } from 'axios';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { useClient, useUpdateClient, type ClientUpdatePayload } from '@/api/clients';
import {
    useClientCustomFieldDefs,
    type CustomFieldValue,
} from '@/api/customFields';
import { useUserDirectory } from '@/api/users';
import { useActiveDiagnoses, useActivePayers, type Payer } from '@/api/referenceData';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import { cn } from '@/lib/cn';
import { SelectField } from './SelectField';
import { US_STATES } from './usStates';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import {
    CustomFieldsForm,
    initCustomFieldsForm,
    validateCustomFieldsForm,
    type CustomFieldFormState,
} from './CustomFieldsForm';

/*
 * Full edit form — mirrors the detail page's tab layout so there's no
 * jarring visual shift when the user clicks Edit. Only tabs that map to
 * editable client fields are shown here (Overview, Demographics, Custom
 * Fields); auths, staff, contact log, and history have their own
 * add/edit flows on the detail page.
 *
 * Card bodies use a 2-column grid so short fields sit side-by-side —
 * the previous one-column-per-card layout was too much vertical
 * scrolling for dense forms like demographics and insurance.
 *
 * Status is NOT editable here. It's changed via the clickable status
 * badge on the detail page, which opens a modal that captures an
 * optional note and writes a history entry atomically.
 */

const TABS = [
    { id: 'overview',     label: 'Overview' },
    { id: 'demographics', label: 'Demographics' },
    { id: 'background',   label: 'Background' },
    { id: 'custom',       label: 'Custom Fields' },
] as const;
type TabId = typeof TABS[number]['id'];

/** Local-time YYYY-MM-DD for `today` — matches what the <input type="date">
 *  picker treats as "today" so the max attribute and the Zod refine agree. */
function todayLocalIso(): string {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

const schema = z.object({
    first:  z.string().trim().min(1, 'First name is required'),
    last:   z.string().trim().min(1, 'Last name is required'),
    dob:    z.string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date of birth is required')
        .refine((v) => v <= todayLocalIso(), 'Date of birth cannot be in the future'),
    gender: z.string().optional(),
    intake: z.string().optional(),
    phone:   z.string().optional(),
    address: z.string().optional(),
    city:    z.string().optional(),
    state:   z.string().optional(),
    zip:     z.string().optional(),

    dx1Id:   z.string().trim().min(1, 'Primary diagnosis is required'),
    dx1Date: z.string().optional(),
    dx1ProviderName: z.string().optional(),
    dx1ProviderNpi:  z.string().optional(),
    dx2Id:   z.string().optional(),
    dx2Date: z.string().optional(),
    dx2ProviderName: z.string().optional(),
    dx2ProviderNpi:  z.string().optional(),
    developmentalHistory:   z.string().optional(),
    medical:                z.string().optional(),
    medications:            z.string().optional(),
    allergies:              z.string().optional(),
    familyMedicalHistory:   z.string().optional(),
    reasonForReferral:      z.string().optional(),
    otherServices:          z.string().optional(),
    communityServices:      z.string().optional(),
    coordinationOfCare:     z.string().optional(),
    education:              z.string().optional(),
    schoolSchedule:         z.string().optional(),
    culturalConsiderations: z.string().optional(),
    bcbaId:  z.string().optional(),

    gname:  z.string().optional(),
    grel:   z.string().optional(),
    gphone: z.string().optional(),
    gemail: z.string().optional().refine(
        (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
        'Enter a valid email',
    ),

    ecName:  z.string().optional(),
    ecRel:   z.string().optional(),
    ecPhone: z.string().optional(),
    ecEmail: z.string().optional().refine(
        (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
        'Enter a valid email',
    ),

    ins1Payer: z.string().optional(),
    ins1Id:    z.string().optional(),
    ins1Group: z.string().optional(),
    ins1Sub:   z.string().optional(),
    ins1SubRel:   z.enum(['self', 'spouse', 'child', 'other']).optional(),
    ins1SubDob:   z.string().optional(),
    ins1SubGender:z.string().optional(),
    ins1SubStreet:z.string().optional(),
    ins1SubCity:  z.string().optional(),
    ins1SubState: z.string().optional(),
    ins1SubZip:   z.string().optional(),

    ins2Payer: z.string().optional(),
    ins2Id:    z.string().optional(),
    ins2Group: z.string().optional(),
    ins2Sub:   z.string().optional(),
    ins2SubRel:   z.enum(['self', 'spouse', 'child', 'other']).optional(),
    ins2SubDob:   z.string().optional(),
    ins2SubGender:z.string().optional(),
    ins2SubStreet:z.string().optional(),
    ins2SubCity:  z.string().optional(),
    ins2SubState: z.string().optional(),
    ins2SubZip:   z.string().optional(),
});
type FormValues = z.infer<typeof schema>;

// Tab → schema fields that live on that tab. Used to surface validation
// errors when the user is on a different tab than the failing field —
// without this, RHF silently refuses to call onSubmit and the Save
// Changes button looks dead. `custom` is empty because custom fields
// validate outside the zod schema and already self-navigate.
const TAB_FIELDS: Record<TabId, ReadonlyArray<keyof FormValues>> = {
    overview: [
        'first', 'last', 'dob', 'gender', 'intake', 'bcbaId',
        'dx1Id', 'dx1Date', 'dx1ProviderName', 'dx1ProviderNpi',
        'dx2Id', 'dx2Date', 'dx2ProviderName', 'dx2ProviderNpi',
        'gname', 'grel', 'gphone', 'gemail',
        'developmentalHistory', 'medical',
    ],
    demographics: [
        'phone', 'address', 'city', 'state', 'zip',
        'ecName', 'ecRel', 'ecPhone', 'ecEmail',
        'ins1Payer', 'ins1Id', 'ins1Group', 'ins1Sub', 'ins1SubRel',
        'ins1SubDob', 'ins1SubGender', 'ins1SubStreet', 'ins1SubCity',
        'ins1SubState', 'ins1SubZip',
        'ins2Payer', 'ins2Id', 'ins2Group', 'ins2Sub', 'ins2SubRel',
        'ins2SubDob', 'ins2SubGender', 'ins2SubStreet', 'ins2SubCity',
        'ins2SubState', 'ins2SubZip',
    ],
    background: [
        'medications', 'allergies', 'familyMedicalHistory',
        'reasonForReferral', 'otherServices', 'communityServices',
        'coordinationOfCare', 'education', 'schoolSchedule',
        'culturalConsiderations',
    ],
    custom: [],
};

function findFirstErroredTab(erroredKeys: ReadonlyArray<string>): TabId | null {
    for (const tab of TABS) {
        const fields = TAB_FIELDS[tab.id] as ReadonlyArray<string>;
        if (erroredKeys.some((k) => fields.includes(k))) return tab.id;
    }
    return null;
}

function blankIfNullish(s: string | null | undefined): string {
    return s ?? '';
}

function toDateInput(s: string | null | undefined): string {
    if (!s) return '';
    return s.slice(0, 10);
}

function initials(first: string, last: string) {
    return (first[0] || '') + (last[0] || '');
}

/**
 * Builds the option list for an insurance-payer dropdown. If the stored
 * payer on the client record isn't present in the active catalog, it's
 * surfaced as "(unlisted)" so the historical value isn't silently lost
 * when the user edits an adjacent field.
 */
function renderPayerOptions(
    payers: Payer[],
    currentPayer: string | undefined,
    isLoading: boolean,
): React.ReactNode {
    const trimmed = (currentPayer || '').trim();
    const inList = trimmed && payers.some((p) => p.name === trimmed);
    return (
        <>
            <option value="">{isLoading ? 'Loading…' : '—'}</option>
            {trimmed && !inList && (
                <option value={trimmed}>{trimmed} (unlisted)</option>
            )}
            {payers.map((p) => (
                <option key={p.id} value={p.name}>{p.name}</option>
            ))}
        </>
    );
}

export function ClientEditPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { data: client, isLoading, isError, error } = useClient(id);
    const updateMut = useUpdateClient(id ?? '');
    const bcbas = useUserDirectory('BCBA');
    const diagnoses = useActiveDiagnoses();
    const payers = useActivePayers();
    const { data: customFieldDefs } = useClientCustomFieldDefs();

    const [customFieldState, setCustomFieldState] = useState<CustomFieldFormState>({});
    const [customFieldErrors, setCustomFieldErrors] = useState<Record<string, string>>({});

    const activeTab: TabId = useMemo(() => {
        const fromHash = location.hash.replace(/^#/, '') as TabId;
        return TABS.some((t) => t.id === fromHash) ? fromHash : 'overview';
    }, [location.hash]);

    useEffect(() => {
        if (!location.hash) {
            navigate({ hash: '#overview' }, { replace: true });
        }
    }, [location.hash, navigate]);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors, isSubmitting },
    } = useForm<FormValues>({ resolver: zodResolver(schema) });

    const ins1Rel = watch('ins1SubRel') ?? 'self';
    const ins2Rel = watch('ins2SubRel') ?? 'self';

    useEffect(() => {
        if (!client) return;
        reset({
            first:  client.first,
            last:   client.last,
            dob:    toDateInput(client.dob),
            gender:         blankIfNullish(client.gender),
            intake:         toDateInput(client.intake),
            phone:   blankIfNullish(client.phone),
            address: blankIfNullish(client.address),
            city:    blankIfNullish(client.city),
            state:   blankIfNullish(client.state),
            zip:     blankIfNullish(client.zip),

            dx1Id:   client.dx1Id ?? '',
            dx1Date: toDateInput(client.dx1Date),
            dx1ProviderName: blankIfNullish(client.dx1ProviderName),
            dx1ProviderNpi:  blankIfNullish(client.dx1ProviderNpi),
            dx2Id:   client.dx2Id ?? '',
            dx2Date: toDateInput(client.dx2Date),
            dx2ProviderName: blankIfNullish(client.dx2ProviderName),
            dx2ProviderNpi:  blankIfNullish(client.dx2ProviderNpi),
            developmentalHistory:   blankIfNullish(client.developmentalHistory),
            medical:                blankIfNullish(client.medical),
            medications:            blankIfNullish(client.medications),
            allergies:              blankIfNullish(client.allergies),
            familyMedicalHistory:   blankIfNullish(client.familyMedicalHistory),
            reasonForReferral:      blankIfNullish(client.reasonForReferral),
            otherServices:          blankIfNullish(client.otherServices),
            communityServices:      blankIfNullish(client.communityServices),
            coordinationOfCare:     blankIfNullish(client.coordinationOfCare),
            education:              blankIfNullish(client.education),
            schoolSchedule:         blankIfNullish(client.schoolSchedule),
            culturalConsiderations: blankIfNullish(client.culturalConsiderations),
            bcbaId:  client.bcbaId ?? '',

            gname:  blankIfNullish(client.gname),
            grel:   blankIfNullish(client.grel),
            gphone: blankIfNullish(client.gphone),
            gemail: blankIfNullish(client.gemail),

            ecName:  blankIfNullish(client.emergencyContact?.name),
            ecRel:   blankIfNullish(client.emergencyContact?.rel),
            ecPhone: blankIfNullish(client.emergencyContact?.phone),
            ecEmail: blankIfNullish(client.emergencyContact?.email),

            ins1Payer: blankIfNullish(client.ins1?.payer),
            ins1Id:    blankIfNullish(client.ins1?.id),
            ins1Group: blankIfNullish(client.ins1?.group),
            ins1Sub:   blankIfNullish(client.ins1?.sub),
            ins1SubRel:    client.ins1?.subRel ?? 'self',
            ins1SubDob:    toDateInput(client.ins1?.subDob),
            ins1SubGender: blankIfNullish(client.ins1?.subGender),
            ins1SubStreet: blankIfNullish(client.ins1?.subAddress?.street),
            ins1SubCity:   blankIfNullish(client.ins1?.subAddress?.city),
            ins1SubState:  blankIfNullish(client.ins1?.subAddress?.state),
            ins1SubZip:    blankIfNullish(client.ins1?.subAddress?.zip),

            ins2Payer: blankIfNullish(client.ins2?.payer),
            ins2Id:    blankIfNullish(client.ins2?.id),
            ins2Group: blankIfNullish(client.ins2?.group),
            ins2Sub:   blankIfNullish(client.ins2?.sub),
            ins2SubRel:    client.ins2?.subRel ?? 'self',
            ins2SubDob:    toDateInput(client.ins2?.subDob),
            ins2SubGender: blankIfNullish(client.ins2?.subGender),
            ins2SubStreet: blankIfNullish(client.ins2?.subAddress?.street),
            ins2SubCity:   blankIfNullish(client.ins2?.subAddress?.city),
            ins2SubState:  blankIfNullish(client.ins2?.subAddress?.state),
            ins2SubZip:    blankIfNullish(client.ins2?.subAddress?.zip),
        });
    }, [client, reset]);

    useEffect(() => {
        if (!client || !customFieldDefs) return;
        setCustomFieldState(
            initCustomFieldsForm(
                customFieldDefs,
                (client.customFields || {}) as Record<string, CustomFieldValue>,
            ),
        );
        setCustomFieldErrors({});
    }, [client, customFieldDefs]);

    // First schema error to surface in a top-of-form banner. Inline
    // FormField errors are tab-scoped — without this banner, a missing
    // required field on a different tab would be invisible to a user
    // editing (say) insurance on Demographics.
    const firstSchemaError = useMemo(() => {
        const keys = Object.keys(errors) as (keyof FormValues)[];
        if (!keys.length) return null;
        const tab = findFirstErroredTab(keys);
        const key = (tab
            ? keys.find((k) => (TAB_FIELDS[tab] as ReadonlyArray<string>).includes(k as string))
            : keys[0]) as keyof FormValues | undefined;
        if (!key) return null;
        return { tab, message: errors[key]?.message ?? 'Some fields need attention before saving.' };
    }, [errors]);

    // Jump the user to the tab that holds the first failing field so
    // the inline FormField error has a DOM home to render in.
    const onError: SubmitErrorHandler<FormValues> = (errs) => {
        const tab = findFirstErroredTab(Object.keys(errs));
        if (tab && tab !== activeTab) {
            navigate({ hash: `#${tab}` }, { replace: true });
        }
    };

    async function onSubmit(v: FormValues) {
        if (!id) return;
        let cfValues: ClientUpdatePayload['customFields'] | undefined;
        if (customFieldDefs) {
            const res = validateCustomFieldsForm(customFieldDefs, customFieldState);
            if (Object.keys(res.errors).length) {
                setCustomFieldErrors(res.errors);
                navigate({ hash: '#custom' }, { replace: true });
                return;
            }
            setCustomFieldErrors({});
            cfValues = res.values;
        }
        const payload: ClientUpdatePayload = {
            first: v.first.trim(),
            last:  v.last.trim(),
            dob:   v.dob,
            gender: v.gender ?? '',
            intake: v.intake || null,
            phone: v.phone ?? '',
            address: v.address ?? '',
            city: v.city ?? '',
            state: v.state ?? '',
            zip: v.zip ?? '',
            dx1Id: v.dx1Id,
            dx1Date: v.dx1Date || null,
            dx1ProviderName: v.dx1ProviderName ?? '',
            dx1ProviderNpi:  v.dx1ProviderNpi ?? '',
            dx2Id: v.dx2Id || null,
            dx2Date: v.dx2Date || null,
            dx2ProviderName: v.dx2ProviderName ?? '',
            dx2ProviderNpi:  v.dx2ProviderNpi ?? '',
            developmentalHistory:   v.developmentalHistory ?? '',
            medical:                v.medical ?? '',
            medications:            v.medications ?? '',
            allergies:              v.allergies ?? '',
            familyMedicalHistory:   v.familyMedicalHistory ?? '',
            reasonForReferral:      v.reasonForReferral ?? '',
            otherServices:          v.otherServices ?? '',
            communityServices:      v.communityServices ?? '',
            coordinationOfCare:     v.coordinationOfCare ?? '',
            education:              v.education ?? '',
            schoolSchedule:         v.schoolSchedule ?? '',
            culturalConsiderations: v.culturalConsiderations ?? '',
            bcbaId: v.bcbaId || null,
            gname:  v.gname ?? '',
            grel:   v.grel ?? '',
            gphone: v.gphone ?? '',
            gemail: v.gemail ?? '',
            emergencyContact: {
                name:  v.ecName ?? '',
                rel:   v.ecRel ?? '',
                phone: v.ecPhone ?? '',
                email: v.ecEmail ?? '',
            },
            ins1: {
                payer: v.ins1Payer ?? '',
                id:    v.ins1Id ?? '',
                group: v.ins1Group ?? '',
                sub:   v.ins1Sub ?? '',
                subRel:    v.ins1SubRel ?? 'self',
                subDob:    v.ins1SubDob || '',
                subGender: v.ins1SubGender ?? '',
                subAddress: {
                    street: v.ins1SubStreet ?? '',
                    city:   v.ins1SubCity ?? '',
                    state:  v.ins1SubState ?? '',
                    zip:    v.ins1SubZip ?? '',
                },
            },
            ins2: (v.ins2Payer || v.ins2Id || v.ins2Group || v.ins2Sub)
                ? {
                    payer: v.ins2Payer ?? '',
                    id:    v.ins2Id ?? '',
                    group: v.ins2Group ?? '',
                    sub:   v.ins2Sub ?? '',
                    subRel:    v.ins2SubRel ?? 'self',
                    subDob:    v.ins2SubDob || '',
                    subGender: v.ins2SubGender ?? '',
                    subAddress: {
                        street: v.ins2SubStreet ?? '',
                        city:   v.ins2SubCity ?? '',
                        state:  v.ins2SubState ?? '',
                        zip:    v.ins2SubZip ?? '',
                    },
                }
                : null,
            ...(cfValues !== undefined ? { customFields: cfValues } : {}),
        };
        try {
            await updateMut.mutateAsync(payload);
            navigate(`/clients/${id}${location.hash || '#overview'}`);
        } catch {
            /* surfaced below */
        }
    }

    function cancelToDetail() {
        navigate(`/clients/${id}${location.hash || '#overview'}`);
    }

    const serverError = (() => {
        const e = updateMut.error;
        if (!e) return null;
        if (e instanceof AxiosError) return e.response?.data?.error ?? 'Failed to save changes';
        return 'Unexpected error';
    })();

    if (isLoading) {
        return (
            <div className="p-12 flex items-center justify-center text-text-subtle">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading client…
            </div>
        );
    }
    if (isError || !client) {
        return (
            <div className="p-6 max-w-3xl mx-auto">
                <Link to="/clients" className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-text mb-3">
                    <ChevronLeft className="w-3 h-3" /> Back to clients
                </Link>
                <Card>
                    <p className="font-medium text-error mb-1">Couldn't load this client.</p>
                    <p className="text-xs text-text-subtle">
                        {extractErrorMessage(error, 'You may not have access, or the record may not exist.')}
                    </p>
                </Card>
            </div>
        );
    }

    const diagnosisOptions = (
        <>
            {(diagnoses.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                    {d.code ? `${d.code} — ${d.name}` : d.name}
                </option>
            ))}
        </>
    );

    return (
        <div className="p-6 max-w-[1400px] mx-auto">
            <Link to={`/clients/${id}`} className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-text mb-3">
                <ChevronLeft className="w-3 h-3" /> Back to {client.first} {client.last}
            </Link>

            <form onSubmit={handleSubmit(onSubmit, onError)} noValidate>
                <Card className="mb-4">
                    <div className="flex items-start gap-4">
                        <div
                            aria-hidden
                            className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent text-[#111] text-base font-bold"
                        >
                            {initials(client.first, client.last)}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h1 className="font-display text-2xl font-bold text-text">
                                Editing {client.first} {client.last}
                            </h1>
                            <p className="text-xs text-text-subtle mt-0.5">
                                Changes are not saved until you click Save Changes.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={cancelToDetail}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" variant="success" size="sm" disabled={isSubmitting}>
                                {isSubmitting ? 'Saving…' : 'Save Changes'}
                            </Button>
                        </div>
                    </div>
                </Card>

                {serverError && <Alert variant="danger" className="mb-4">{serverError}</Alert>}
                {firstSchemaError && (
                    <Alert variant="danger" className="mb-4" data-testid="schema-error-banner">
                        {firstSchemaError.message}
                    </Alert>
                )}

                <div role="tablist" aria-label="Client edit sections" className="flex flex-wrap gap-1 mb-3 border-b border-border">
                    {TABS.map((t) => (
                        <Link
                            key={t.id}
                            to={{ hash: `#${t.id}` }}
                            replace
                            role="tab"
                            aria-selected={activeTab === t.id}
                            className={cn(
                                'px-3 py-2 text-xs font-medium -mb-px border-b-2 transition-colors',
                                activeTab === t.id
                                    ? 'border-accent text-text'
                                    : 'border-transparent text-text-muted hover:text-text',
                            )}
                        >
                            {t.label}
                        </Link>
                    ))}
                </div>

                {activeTab === 'overview' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Card>
                            <CardHeader><CardTitle>Snapshot</CardTitle></CardHeader>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                                <FormField label="First name" required error={errors.first?.message}>
                                    {(id) => <Input id={id} {...register('first')} />}
                                </FormField>
                                <FormField label="Last name" required error={errors.last?.message}>
                                    {(id) => <Input id={id} {...register('last')} />}
                                </FormField>
                                <FormField label="Date of birth" required error={errors.dob?.message}>
                                    {(id) => <Input id={id} type="date" min="1900-01-01" max={todayLocalIso()} {...register('dob')} />}
                                </FormField>
                                <FormField label="Gender">
                                    {(id) => <Input id={id} {...register('gender')} />}
                                </FormField>
                                <FormField label="Intake date">
                                    {(id) => <Input id={id} type="date" min="1900-01-01" max={todayLocalIso()} {...register('intake')} />}
                                </FormField>
                                <FormField label="Supervising BCBA">
                                    {(id) => (
                                        <SelectField id={id} {...register('bcbaId')}>
                                            <option value="">Unassigned</option>
                                            {(bcbas.data ?? []).map((u) => (
                                                <option key={u.id} value={u.id}>
                                                    {u.first} {u.last}{u.roleName !== 'BCBA' ? ` — ${u.roleName}` : ''}
                                                </option>
                                            ))}
                                        </SelectField>
                                    )}
                                </FormField>
                            </div>
                        </Card>

                        <Card>
                            <CardHeader><CardTitle>Diagnoses</CardTitle></CardHeader>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                                <FormField label="Primary diagnosis" required error={errors.dx1Id?.message}>
                                    {(id) => (
                                        <SelectField id={id} {...register('dx1Id')}>
                                            <option value="">{diagnoses.isLoading ? 'Loading…' : 'Select a diagnosis'}</option>
                                            {diagnosisOptions}
                                        </SelectField>
                                    )}
                                </FormField>
                                <FormField label="Primary dx date">
                                    {(id) => <Input id={id} type="date" min="1900-01-01" max={todayLocalIso()} {...register('dx1Date')} />}
                                </FormField>
                                <FormField label="Primary diagnosing provider">
                                    {(id) => <Input id={id} {...register('dx1ProviderName')} />}
                                </FormField>
                                <FormField label="Primary provider NPI">
                                    {(id) => <Input id={id} inputMode="numeric" maxLength={10} {...register('dx1ProviderNpi')} />}
                                </FormField>
                                <FormField label="Secondary diagnosis">
                                    {(id) => (
                                        <SelectField id={id} {...register('dx2Id')}>
                                            <option value="">None</option>
                                            {diagnosisOptions}
                                        </SelectField>
                                    )}
                                </FormField>
                                <FormField label="Secondary dx date">
                                    {(id) => <Input id={id} type="date" min="1900-01-01" max={todayLocalIso()} {...register('dx2Date')} />}
                                </FormField>
                                <FormField label="Secondary diagnosing provider">
                                    {(id) => <Input id={id} {...register('dx2ProviderName')} />}
                                </FormField>
                                <FormField label="Secondary provider NPI">
                                    {(id) => <Input id={id} inputMode="numeric" maxLength={10} {...register('dx2ProviderNpi')} />}
                                </FormField>
                            </div>
                        </Card>

                        <Card className="lg:col-span-2">
                            <CardHeader><CardTitle>Parent/Guardian</CardTitle></CardHeader>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                                <FormField label="Name">
                                    {(id) => <Input id={id} {...register('gname')} />}
                                </FormField>
                                <FormField label="Relationship">
                                    {(id) => <Input id={id} {...register('grel')} />}
                                </FormField>
                                <FormField label="Phone">
                                    {(id) => <Input id={id} type="tel" autoComplete="off" {...register('gphone')} />}
                                </FormField>
                                <FormField label="Email" error={errors.gemail?.message}>
                                    {(id) => <Input id={id} type="email" autoComplete="off" {...register('gemail')} />}
                                </FormField>
                            </div>
                        </Card>

                        <Card className="lg:col-span-2">
                            <CardHeader><CardTitle>Clinical Notes</CardTitle></CardHeader>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField label="Developmental history" className="mb-0">
                                    {(id) => <Textarea id={id} rows={4} {...register('developmentalHistory')} />}
                                </FormField>
                                <FormField label="Medical" className="mb-0">
                                    {(id) => <Textarea id={id} rows={4} {...register('medical')} />}
                                </FormField>
                            </div>
                        </Card>
                    </div>
                )}

                {activeTab === 'demographics' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Card>
                            <CardHeader><CardTitle>Contact</CardTitle></CardHeader>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                                <FormField label="Phone">
                                    {(id) => <Input id={id} type="tel" autoComplete="off" {...register('phone')} />}
                                </FormField>
                                <div />
                                <FormField label="Address" className="sm:col-span-2">
                                    {(id) => <Input id={id} {...register('address')} />}
                                </FormField>
                                <FormField label="City">
                                    {(id) => <Input id={id} {...register('city')} />}
                                </FormField>
                                <div className="grid grid-cols-2 gap-x-4">
                                    <FormField label="State">
                                        {(id) => (
                                            <SelectField id={id} {...register('state')}>
                                                <option value="">—</option>
                                                {US_STATES.map((s) => (
                                                    <option key={s.code} value={s.code}>
                                                        {s.code} — {s.name}
                                                    </option>
                                                ))}
                                            </SelectField>
                                        )}
                                    </FormField>
                                    <FormField label="ZIP">
                                        {(id) => <Input id={id} {...register('zip')} />}
                                    </FormField>
                                </div>
                            </div>
                        </Card>

                        <Card>
                            <CardHeader><CardTitle>Emergency Contact</CardTitle></CardHeader>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                                <FormField label="Name">
                                    {(id) => <Input id={id} {...register('ecName')} />}
                                </FormField>
                                <FormField label="Relationship">
                                    {(id) => <Input id={id} {...register('ecRel')} />}
                                </FormField>
                                <FormField label="Phone">
                                    {(id) => <Input id={id} type="tel" autoComplete="off" {...register('ecPhone')} />}
                                </FormField>
                                <FormField label="Email" error={errors.ecEmail?.message}>
                                    {(id) => <Input id={id} type="email" autoComplete="off" {...register('ecEmail')} />}
                                </FormField>
                            </div>
                        </Card>

                        <Card>
                            <CardHeader><CardTitle>Primary Insurance</CardTitle></CardHeader>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                                <FormField label="Payer">
                                    {(id) => (
                                        <SelectField id={id} {...register('ins1Payer')} disabled={payers.isLoading}>
                                            {renderPayerOptions(payers.data ?? [], client.ins1?.payer, payers.isLoading)}
                                        </SelectField>
                                    )}
                                </FormField>
                                <FormField label="Member ID">
                                    {(id) => <Input id={id} {...register('ins1Id')} />}
                                </FormField>
                                <FormField label="Group">
                                    {(id) => <Input id={id} {...register('ins1Group')} />}
                                </FormField>
                                <FormField
                                    label="Subscriber relationship"
                                    hint="If the subscriber is not the patient, fill the subscriber block below."
                                >
                                    {(id) => (
                                        <SelectField id={id} {...register('ins1SubRel')}>
                                            <option value="self">Self (patient is subscriber)</option>
                                            <option value="spouse">Spouse</option>
                                            <option value="child">Child</option>
                                            <option value="other">Other</option>
                                        </SelectField>
                                    )}
                                </FormField>
                            </div>
                            {ins1Rel !== 'self' && (
                                <div className="mt-2 pt-3 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                                    <FormField label="Subscriber name" className="sm:col-span-2">
                                        {(id) => <Input id={id} {...register('ins1Sub')} placeholder="First Last" />}
                                    </FormField>
                                    <FormField label="Subscriber DOB">
                                        {(id) => <Input id={id} type="date" min="1900-01-01" max={todayLocalIso()} {...register('ins1SubDob')} />}
                                    </FormField>
                                    <FormField label="Subscriber gender">
                                        {(id) => (
                                            <SelectField id={id} {...register('ins1SubGender')}>
                                                <option value="">—</option>
                                                <option value="M">Male</option>
                                                <option value="F">Female</option>
                                                <option value="U">Unknown</option>
                                            </SelectField>
                                        )}
                                    </FormField>
                                    <FormField label="Subscriber street" className="sm:col-span-2">
                                        {(id) => <Input id={id} {...register('ins1SubStreet')} />}
                                    </FormField>
                                    <FormField label="City">
                                        {(id) => <Input id={id} {...register('ins1SubCity')} />}
                                    </FormField>
                                    <FormField label="State">
                                        {(id) => (
                                            <SelectField id={id} {...register('ins1SubState')}>
                                                <option value="">—</option>
                                                {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.code}</option>)}
                                            </SelectField>
                                        )}
                                    </FormField>
                                    <FormField label="ZIP">
                                        {(id) => <Input id={id} {...register('ins1SubZip')} />}
                                    </FormField>
                                </div>
                            )}
                        </Card>

                        <Card>
                            <CardHeader><CardTitle>Secondary Insurance</CardTitle></CardHeader>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                                <FormField label="Payer">
                                    {(id) => (
                                        <SelectField id={id} {...register('ins2Payer')} disabled={payers.isLoading}>
                                            {renderPayerOptions(payers.data ?? [], client.ins2?.payer, payers.isLoading)}
                                        </SelectField>
                                    )}
                                </FormField>
                                <FormField label="Member ID">
                                    {(id) => <Input id={id} {...register('ins2Id')} />}
                                </FormField>
                                <FormField label="Group">
                                    {(id) => <Input id={id} {...register('ins2Group')} />}
                                </FormField>
                                <FormField
                                    label="Subscriber relationship"
                                    hint="If the subscriber is not the patient, fill the subscriber block below."
                                >
                                    {(id) => (
                                        <SelectField id={id} {...register('ins2SubRel')}>
                                            <option value="self">Self (patient is subscriber)</option>
                                            <option value="spouse">Spouse</option>
                                            <option value="child">Child</option>
                                            <option value="other">Other</option>
                                        </SelectField>
                                    )}
                                </FormField>
                            </div>
                            {ins2Rel !== 'self' && (
                                <div className="mt-2 pt-3 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                                    <FormField label="Subscriber name" className="sm:col-span-2">
                                        {(id) => <Input id={id} {...register('ins2Sub')} placeholder="First Last" />}
                                    </FormField>
                                    <FormField label="Subscriber DOB">
                                        {(id) => <Input id={id} type="date" min="1900-01-01" max={todayLocalIso()} {...register('ins2SubDob')} />}
                                    </FormField>
                                    <FormField label="Subscriber gender">
                                        {(id) => (
                                            <SelectField id={id} {...register('ins2SubGender')}>
                                                <option value="">—</option>
                                                <option value="M">Male</option>
                                                <option value="F">Female</option>
                                                <option value="U">Unknown</option>
                                            </SelectField>
                                        )}
                                    </FormField>
                                    <FormField label="Subscriber street" className="sm:col-span-2">
                                        {(id) => <Input id={id} {...register('ins2SubStreet')} />}
                                    </FormField>
                                    <FormField label="City">
                                        {(id) => <Input id={id} {...register('ins2SubCity')} />}
                                    </FormField>
                                    <FormField label="State">
                                        {(id) => (
                                            <SelectField id={id} {...register('ins2SubState')}>
                                                <option value="">—</option>
                                                {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.code}</option>)}
                                            </SelectField>
                                        )}
                                    </FormField>
                                    <FormField label="ZIP">
                                        {(id) => <Input id={id} {...register('ins2SubZip')} />}
                                    </FormField>
                                </div>
                            )}
                        </Card>
                    </div>
                )}

                {activeTab === 'background' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Card className="lg:col-span-2">
                            <CardHeader><CardTitle>Referral & Care Coordination</CardTitle></CardHeader>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                                <FormField label="Reason for referral" className="md:col-span-2">
                                    {(id) => <Textarea id={id} rows={3} {...register('reasonForReferral')} />}
                                </FormField>
                                <FormField label="Other services (OT, PT, SLP, etc.)">
                                    {(id) => <Textarea id={id} rows={3} {...register('otherServices')} />}
                                </FormField>
                                <FormField label="Community services">
                                    {(id) => <Textarea id={id} rows={3} {...register('communityServices')} />}
                                </FormField>
                                <FormField label="Coordination of care" className="md:col-span-2 mb-0">
                                    {(id) => <Textarea id={id} rows={3} {...register('coordinationOfCare')} />}
                                </FormField>
                            </div>
                        </Card>

                        <Card>
                            <CardHeader><CardTitle>Medical Background</CardTitle></CardHeader>
                            <div className="grid grid-cols-1 gap-x-4">
                                <FormField label="Current medications">
                                    {(id) => <Textarea id={id} rows={3} {...register('medications')} />}
                                </FormField>
                                <FormField label="Allergies">
                                    {(id) => <Textarea id={id} rows={2} {...register('allergies')} />}
                                </FormField>
                                <FormField label="Family medical history" className="mb-0">
                                    {(id) => <Textarea id={id} rows={3} {...register('familyMedicalHistory')} />}
                                </FormField>
                            </div>
                        </Card>

                        <Card>
                            <CardHeader><CardTitle>School & Culture</CardTitle></CardHeader>
                            <div className="grid grid-cols-1 gap-x-4">
                                <FormField label="Education (school / grade / program)">
                                    {(id) => <Textarea id={id} rows={3} {...register('education')} />}
                                </FormField>
                                <FormField label="School schedule">
                                    {(id) => <Textarea id={id} rows={3} {...register('schoolSchedule')} />}
                                </FormField>
                                <FormField label="Cultural / spiritual considerations" className="mb-0">
                                    {(id) => <Textarea id={id} rows={3} {...register('culturalConsiderations')} />}
                                </FormField>
                            </div>
                        </Card>
                    </div>
                )}

                {activeTab === 'custom' && (
                    <CustomFieldsForm
                        defs={customFieldDefs ?? []}
                        value={customFieldState}
                        onChange={setCustomFieldState}
                        errors={customFieldErrors}
                        disabled={isSubmitting}
                    />
                )}

                <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-border">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={cancelToDetail}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button type="submit" variant="success" disabled={isSubmitting}>
                        {isSubmitting ? 'Saving…' : 'Save Changes'}
                    </Button>
                </div>
            </form>
        </div>
    );
}
